/**
 * Shared <FunctionSourceEditor> primitive — Monaco editor wrapper for
 * function source bodies (Wave C PR-F functions UI parity, 2026-05-13).
 *
 * Consolidates THREE pre-existing Monaco call sites:
 *   1. routes/connections/functions.tsx (tenant Sheet — view/edit deployed
 *      tenant_functions source)
 *   2. routes/sysadmin/catalog.functions.index.tsx Source tab in
 *      FunctionDetailDialog (sysadmin — edit + sign + activate flow)
 *   3. routes/sysadmin/catalog.functions.index.tsx NewFunctionSheet
 *      (sysadmin — classic-form create flow)
 *
 * Each call site had its own inline <Editor> with its own ~6-14 option
 * block + language-mapping function + theme decision. The pre-refactor
 * footgun: drift between the three blocks (font sizes, minimap toggles,
 * theme hard-coding) gradually diverged the visual rendering. This
 * primitive captures all three presets behind a `variant` discriminator
 * so the SAME component renders each surface byte-identically to its
 * pre-refactor state.
 *
 * What is NOT shared (correctly scope-specific):
 *   - The Sheet/Dialog/Tab outer chrome (each surface paints its own)
 *   - Toolbar buttons (Save / Sign / Format / Copy / Deploy / Test)
 *   - Status banners (read-only banner; native-function banner)
 *   - Test output panel (tenant only)
 *   - Lifecycle action bar (sysadmin only)
 *   - Sysadmin signing key / publishing UI
 *
 * Per `feedback_shared_component_scope_prop.md`: one shared primitive,
 * NOT three parallel copies. Per the inventory memo, Functions has LOW
 * 30-40% overlap because the LIST pages serve different products — only
 * the editor inner block is genuinely shared.
 *
 * The richer authoring page already shares via <ToolDeveloperPage scope>
 * (see routes/connections/new-function.tsx + routes/sysadmin/catalog.
 * functions.new.tsx, both ~70-450 LOC thin wrappers).
 */
import Editor from '@monaco-editor/react';
import {
  buildFunctionEditorOptions,
  functionLanguageToMonacoLang,
  resolveMonacoTheme,
  variantDefaultContainerClass,
  variantDefaultHeight,
} from './FunctionSourceEditor.helpers';
import type { FunctionSourceEditorProps } from './FunctionSourceEditor.types';

export function FunctionSourceEditor({
  value,
  onChange,
  language,
  variant,
  readOnly = false,
  theme,
  fullscreen = false,
  height,
  containerClassName,
  onMount,
}: FunctionSourceEditorProps) {
  const resolvedHeight = height ?? variantDefaultHeight(variant);
  // Container class only wraps the editor for sysadmin variants — tenant
  // Sheet expands into its parent flex container directly. See helper
  // doc for the rationale (Tailwind JIT can't see runtime height strings).
  const wrapperClass =
    containerClassName ?? variantDefaultContainerClass(variant, resolvedHeight);

  const editorTheme = resolveMonacoTheme(variant, theme);
  const editorOptions = buildFunctionEditorOptions({
    variant,
    language,
    readOnly,
    fullscreen,
  });
  const monacoLang = functionLanguageToMonacoLang(language);

  const editorEl = (
    <Editor
      height={resolvedHeight}
      language={monacoLang}
      value={value}
      theme={editorTheme}
      onChange={(v) => onChange(v ?? '')}
      onMount={onMount}
      options={editorOptions}
    />
  );

  // When the variant supplies a wrapper class (sysadmin Source tab +
  // NewFunctionSheet), pass through the fixed height the call site
  // historically used. The wrapper height was previously set with
  // Tailwind `h-[420px]` / `h-[300px]` arbitrary values; we reproduce
  // that here via inline style so the variant-default switch stays the
  // single source of truth for sizing. Tenant variant returns '' so no
  // wrapper is rendered.
  if (wrapperClass === '') {
    return editorEl;
  }

  return (
    <div className={wrapperClass} style={{ height: resolvedHeight }}>
      {editorEl}
    </div>
  );
}

// Re-exports for callers that want the discriminator types.
export type {
  FunctionEditorLanguage,
  FunctionEditorVariant,
  FunctionSourceEditorProps,
} from './FunctionSourceEditor.types';
